import React, { useState, useEffect } from 'react';
import './canvas-integration.css';

/**
 * Canvas LMS Integration Component
 * 
 * This component provides UI for:
 * 1. Connecting to Canvas LMS
 * 2. Viewing connected courses
 * 3. Deploying Morpheus tutors to Canvas courses
 * 4. Managing institutional settings
 */
const CanvasIntegration = ({ user }) => {
  // States for Canvas integration
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('');
  const [courses, setCourses] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentResult, setDeploymentResult] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [institutionalMode, setInstitutionalMode] = useState(false);
  const [tokenName, setTokenName] = useState('Tokens');
  const [canvasUrl, setCanvasUrl] = useState('https://canvas.instructure.com');

  // Check connection status on load
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  // Check if connected to Canvas
  const checkConnectionStatus = async () => {
    setConnectionStatus('Checking connection...');
    
    try {
      const response = await fetch('/api/lms/canvas/status');
      const data = await response.json();
      
      setIsConnected(data.connected);
      setConnectionStatus(data.connected ? 'Connected to Canvas' : 'Not connected to Canvas');
      
      if (data.connected) {
        fetchCourses();
        fetchModels();
      }
    } catch (error) {
      console.error('Error checking Canvas connection:', error);
      setConnectionStatus('Error checking connection');
      setIsConnected(false);
    }
  };

  // Connect to Canvas with API key
  const connectToCanvas = async () => {
    if (!apiKey) {
      setConnectionStatus('API key is required');
      return;
    }
    
    setConnectionStatus('Connecting to Canvas...');
    
    try {
      const response = await fetch('/api/lms/canvas/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiKey,
          canvasUrl,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsConnected(true);
        setConnectionStatus('Successfully connected to Canvas');
        fetchCourses();
        fetchModels();
      } else {
        setConnectionStatus(`Failed to connect: ${data.error}`);
      }
    } catch (error) {
      console.error('Error connecting to Canvas:', error);
      setConnectionStatus('Error connecting to Canvas');
    }
  };

  // Fetch courses from Canvas
  const fetchCourses = async () => {
    try {
      const response = await fetch('/api/lms/canvas/courses');
      const data = await response.json();
      
      if (data.success) {
        setCourses(data.courses);
      } else {
        console.error('Failed to fetch courses:', data.error);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Fetch available models
  const fetchModels = async () => {
    try {
      const response = await fetch('/api/models/list');
      const data = await response.json();
      
      if (data.success) {
        setModels(data.models);
      } else {
        console.error('Failed to fetch models:', data.error);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  // Deploy model to Canvas course
  const deployModel = async () => {
    if (!selectedCourseId) {
      setDeploymentResult({ success: false, message: 'Please select a course' });
      return;
    }
    
    if (!selectedModelId) {
      setDeploymentResult({ success: false, message: 'Please select a model' });
      return;
    }
    
    setIsDeploying(true);
    setDeploymentResult(null);
    
    try {
      const response = await fetch('/api/lms/canvas/deploy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          courseId: selectedCourseId,
          modelId: selectedModelId,
          institutionalMode,
          tokenName,
        }),
      });
      
      const data = await response.json();
      
      setDeploymentResult({
        success: data.success,
        message: data.success 
          ? `Successfully deployed ${selectedModelId} to course ${selectedCourseId}` 
          : `Deployment failed: ${data.error}`,
      });
    } catch (error) {
      console.error('Error deploying model:', error);
      setDeploymentResult({
        success: false,
        message: 'Error deploying model',
      });
    } finally {
      setIsDeploying(false);
    }
  };

  // Handle OAuth2 authorization
  const handleOAuth2Login = () => {
    window.location.href = '/api/lms/canvas/oauth2/authorize';
  };

  return (
    <div className="canvas-integration">
      <h2>Canvas LMS Integration</h2>
      
      <div className="connection-status">
        <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? 'Connected' : 'Disconnected'}
        </div>
        <p>{connectionStatus}</p>
      </div>
      
      {!isConnected && (
        <div className="connection-form">
          <h3>Connect to Canvas</h3>
          
          <div className="connection-methods">
            <div className="api-key-method">
              <h4>Connect with API Key</h4>
              <div className="form-group">
                <label htmlFor="canvasUrl">Canvas URL:</label>
                <input
                  type="text"
                  id="canvasUrl"
                  value={canvasUrl}
                  onChange={(e) => setCanvasUrl(e.target.value)}
                  placeholder="https://canvas.instructure.com"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="apiKey">API Key:</label>
                <input
                  type="password"
                  id="apiKey"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your Canvas API key"
                />
              </div>
              
              <button 
                className="connect-button"
                onClick={connectToCanvas}
                disabled={!apiKey}
              >
                Connect with API Key
              </button>
            </div>
            
            <div className="oauth-method">
              <h4>Connect with OAuth2</h4>
              <p>For institutional access, connect using OAuth2 authorization.</p>
              <button 
                className="oauth-button"
                onClick={handleOAuth2Login}
              >
                Authorize with Canvas
              </button>
            </div>
          </div>
        </div>
      )}
      
      {isConnected && (
        <div className="integration-panel">
          <div className="courses-section">
            <h3>Your Canvas Courses</h3>
            
            {courses.length === 0 ? (
              <p>No courses found. Create a course in Canvas first.</p>
            ) : (
              <div className="course-list">
                <select
                  value={selectedCourseId}
                  onChange={(e) => setSelectedCourseId(e.target.value)}
                >
                  <option value="">Select a course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <div className="models-section">
            <h3>Available Models</h3>
            
            {models.length === 0 ? (
              <p>No models available. Train or import a model first.</p>
            ) : (
              <div className="model-list">
                <select
                  value={selectedModelId}
                  onChange={(e) => setSelectedModelId(e.target.value)}
                >
                  <option value="">Select a model</option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({model.specialty})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <div className="institutional-settings">
            <h3>Institutional Settings</h3>
            
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={institutionalMode}
                  onChange={(e) => setInstitutionalMode(e.target.checked)}
                />
                Enable Institutional Mode
              </label>
            </div>
            
            {institutionalMode && (
              <div className="form-group">
                <label htmlFor="tokenName">Token Display Name:</label>
                <input
                  type="text"
                  id="tokenName"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  placeholder="Points, Stars, etc."
                />
              </div>
            )}
          </div>
          
          <div className="deployment-section">
            <button
              className="deploy-button"
              onClick={deployModel}
              disabled={!selectedCourseId || !selectedModelId || isDeploying}
            >
              {isDeploying ? 'Deploying...' : 'Deploy to Canvas'}
            </button>
            
            {deploymentResult && (
              <div className={`deployment-result ${deploymentResult.success ? 'success' : 'error'}`}>
                {deploymentResult.message}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CanvasIntegration;